<template>
	<!-- Under the preview, not in the settings panel.
	-- 
	-- This is the one control on the page whose effect you judge by LOOKING
	-- at the picture, so it belongs beside the picture. Three groups down in
	-- "size and shape", between the crop and the rotate buttons, the thing
	-- being adjusted was off the top of the panel. -->
	<div class="flex flex-col gap-2 w-full">
		<div class="flex flex-wrap items-center justify-center gap-2">
			<button v-for="c in OVERLAY_COLOURS" :key="c.hex"
				@click.prevent="pick(c.hex)"
				:title="t('options.overlayColour', { name: t('colours.' + c.key) })"
				:aria-label="t('options.overlayColour', { name: t('colours.' + c.key) })"
				:aria-pressed="uiStore.overlayColor === c.hex ? 'true' : 'false'"
				class="h-7 w-7 rounded-full border-2 transition"
				:class="uiStore.overlayColor === c.hex ? 'border-white scale-110' : 'border-white/40'"
				:style="{backgroundColor: c.hex}"></button>
		</div>
		<!-- Named, not just a percentage.
		--
		-- The name existed already as the slider's aria-label, so a screen
		-- reader was told what this was and nobody else. On its own line it
		-- pushed the slider past the bottom of a 1000px viewport and the
		-- end-to-end case that keeps look-drag-press within one screen failed
		-- on it -- so it sits in the row, where it costs no height at all. -->
		<label class="flex items-center gap-3">
			<span class="shrink-0 text-[13px] text-ink-soft">{{ t('options.overlayStrength') }}</span>
			<!-- `input` moves the store, which the CSS preview follows for
			-- nothing. `change` fires once, on release, and is the only
			-- thing that re-transforms and re-uploads. -->
			<input type="range" min="0" max="100" step="5"
				:value="Math.round(uiStore.overlayOpacity * 100)"
				@input="uiStore.overlayOpacity = Number($event.target.value) / 100"
				@change="$emit('change')"
				class="range range-sm grow"
				:aria-label="t('options.overlayStrength')" />
			<span class="w-11 shrink-0 text-right text-[13px] font-bold tabular-nums scrim-value">
				{{ Math.round(uiStore.overlayOpacity * 100) }}%
			</span>
		</label>
	</div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { OVERLAY_COLOURS } from "../lib/overlay"
import { useUiElements } from "../store/uiElements"

const emit = defineEmits(['change'])
const uiStore = useUiElements()
const { t } = useI18n()

// A colour is one gesture, so it asks for the work straight away. The slider
// is a drag, which is why it waits for `change`.
function pick(hex) {
	uiStore.overlayColor = hex
	emit('change')
}
</script>

<style scoped>
/* Same fault as the watermark control had: this sits below the drop zone, on
 * the page background, so white lettering disappeared in the light theme. */
.scrim-value { color: var(--ink); }
</style>
